import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, BehaviorSubject } from 'rxjs';
import { environment } from '../../environments/environment';
import { TagReplacement } from '../interfaces/ai-content.interface';

// Comprehensive three-context interfaces
export interface ThreeContextChangeRequestInput {
  user_request: string;
  supplier_data: any;
  semantic_content: { [tagName: string]: string };
}

export interface ChangeImpactSummary {
  supplierFieldsModified: string[];
  semanticTagsUpdated: string[];
  changeImpactLevel: 'HIGH' | 'MEDIUM' | 'LOW';
  totalUpdates: number;
}

export interface ComprehensiveChangeRequestResponse {
  status: 'success' | 'clarification_needed' | 'error';
  message: string;
  updatedSupplierData: any;
  updatedSemanticContent: { [tagName: string]: string };
  changeImpactSummary: ChangeImpactSummary;
  requiresPreviewRefresh: boolean;
  clarificationRequest?: string;
  error?: string;
}

@Injectable({
  providedIn: 'root'
})
export class ChangeRequestService {
  private apiUrl = environment.fastApiBaseUrl;
  
  // Observables for tracking change request state
  private isProcessingSubject = new BehaviorSubject<boolean>(false);
  public isProcessing$ = this.isProcessingSubject.asObservable();

  constructor(private http: HttpClient) {}

  /**
   * Process a user change request with comprehensive three-context approach
   */
  async processChangeRequestComprehensive(
    userRequest: string, 
    supplierData: any, 
    semanticContent: { [tagName: string]: string }
  ): Promise<ComprehensiveChangeRequestResponse> {
    try {
      this.isProcessingSubject.next(true);
      
      console.log('🔄 Processing comprehensive change request:', userRequest);
      console.log('📊 Supplier data fields:', Object.keys(supplierData));
      console.log('🏷️ Semantic content tags:', Object.keys(semanticContent));
      
      const requestData: ThreeContextChangeRequestInput = {
        user_request: userRequest,
        supplier_data: supplierData,
        semantic_content: semanticContent
      };

      const response = await this.http.post<ComprehensiveChangeRequestResponse>(
        `${this.apiUrl}/ai/change-request`,
        requestData
      ).toPromise();

      if (!response) {
        throw new Error('No response received from change request API');
      }

      console.log('✅ Comprehensive change request processed:', response.status);
      console.log(`📈 Impact level: ${response.changeImpactSummary?.changeImpactLevel}`);
      console.log(`🔄 Total updates: ${response.changeImpactSummary?.totalUpdates}`);
      
      return response;

    } catch (error) {
      console.error('❌ Comprehensive change request processing failed:', error);
      
      // Return error response
      return {
        status: 'error',
        message: 'I encountered an error processing your change request. Please try again.',
        updatedSupplierData: {},
        updatedSemanticContent: {},
        changeImpactSummary: {
          supplierFieldsModified: [],
          semanticTagsUpdated: [],
          changeImpactLevel: 'LOW',
          totalUpdates: 0
        },
        requiresPreviewRefresh: false,
        clarificationRequest: undefined,
        error: error instanceof Error ? error.message : 'Unknown error'
      };
    } finally {
      this.isProcessingSubject.next(false);
    }
  }

  /**
   * Apply comprehensive changes to both supplier data and semantic content
   */
  applyComprehensiveChanges(
    currentSupplierData: any,
    currentSemanticContent: { [tagName: string]: string },
    changeResponse: ComprehensiveChangeRequestResponse
  ): { updatedSupplierData: any; updatedSemanticContent: { [tagName: string]: string } } {
    
    // Apply supplier data updates
    const updatedSupplierData = { 
      ...currentSupplierData, 
      ...changeResponse.updatedSupplierData 
    };
    
    // Apply semantic content updates
    const updatedSemanticContent = { 
      ...currentSemanticContent, 
      ...changeResponse.updatedSemanticContent 
    };
    
    console.log('✅ Applied comprehensive changes:');
    console.log(`📊 Supplier fields updated: ${changeResponse.changeImpactSummary.supplierFieldsModified.join(', ')}`);
    console.log(`🏷️ Semantic tags updated: ${changeResponse.changeImpactSummary.semanticTagsUpdated.join(', ')}`);
    
    return { updatedSupplierData, updatedSemanticContent };
  }

  /**
   * Convert TagReplacement array to semantic content object
   */
  convertTagReplacementsToSemanticContent(tagReplacements: TagReplacement[]): { [tagName: string]: string } {
    const semanticContent: { [tagName: string]: string } = {};
    
    for (const replacement of tagReplacements) {
      semanticContent[replacement.tagName] = replacement.replacementHTML;
    }
    
    console.log(`🔄 Converted ${tagReplacements.length} tag replacements to semantic content`);
    return semanticContent;
  }

  /**
   * Convert semantic content object back to TagReplacement array
   */
  convertSemanticContentToTagReplacements(semanticContent: { [tagName: string]: string }): TagReplacement[] {
    const tagReplacements: TagReplacement[] = [];
    
    for (const [tagName, content] of Object.entries(semanticContent)) {
      tagReplacements.push({
        tagName,
        replacementHTML: content
      });
    }
    
    console.log(`🔄 Converted semantic content to ${tagReplacements.length} tag replacements`);
    return tagReplacements;
  }

  /**
   * Check if a field requires content regeneration
   */
  isContentField(fieldName: string): boolean {
    const contentFields = ['companyName', 'aboutContent', 'capabilities', 'equipment'];
    return contentFields.includes(fieldName);
  }

  /**
   * Check if a field is a simple data field
   */
  isSimpleField(fieldName: string): boolean {
    const simpleFields = [
      'phoneNumber', 'contactEmail', 
      'streetAddress', 'streetAddress2', 'city', 
      'countryRegion', 'postalCode', 'websiteURL'
    ];
    return simpleFields.includes(fieldName);
  }

  /**
   * Reset processing state
   */
  resetState(): void {
    this.isProcessingSubject.next(false);
  }
} 